/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import * as yup from "yup";
import {
  LINK_DOCUSIGN_FALLBACK,
  STATUS_LABELS,
  STATUS_CORES,
} from "../../constants/contratos.js";
import {
  formatarDataBR,
  formatarTelefone,
  formatarCnpj,
  normalizeCPF,
  isMenorDeIdade,
} from "../../utils/formatters.js";

export {
  LINK_DOCUSIGN_FALLBACK,
  STATUS_LABELS,
  STATUS_CORES,
  formatarDataBR,
  formatarTelefone,
  formatarCnpj,
  normalizeCPF,
  isMenorDeIdade,
};

// ============================================
// ESQUEMA DE VALIDAÇÃO (YUP)
// ============================================

export const schema = yup.object().shape({
  email: yup
    .string()
    .email("O campo e-mail é inválido")
    .required("O campo e-mail é obrigatório"),
});

// ============================================
// ANEXOS OBRIGATÓRIOS DO TCE
// ============================================

export const ANEXOS_SCHEMA_TCE = [
  {
    id: "declaracao_escolar",
    label: "Declaração escolar e/ou histórico de conclusão do Ensino Médio",
    icon: "GraduationCap",
    required: true,
  },
  {
    // `id` amigável (front-only). O back armazena na coluna
    // `anexo_declaracao_carga_horaria_*` (não em `anexo_carga_horaria_*`).
    // `idColumn` faz `buildAnexosObrigatoriosBodyPatch` e
    // `hydrateAnexosFilesFromPayload` espelharem todas as variações
    // de chave também com o nome real da coluna.
    id: "carga_horaria",
    idColumn: "declaracao_carga_horaria",
    label: "Declaração constando carga horária obrigatória",
    icon: "FileText",
    required: true,
  },
  {
    id: "documento_identificacao",
    label: "Documento de Identificação (RG ou CNH)",
    icon: "IdCard",
    required: true,
  },
];

// ============================================


/**
 * Formata um número (ou string numérica) para o padrão monetário
 * brasileiro: "R$ 1.650,00".
 *
 *   - Ponto separa milhares (1.650).
 *   - Vírgula separa centavos (1.650,00).
 *   - Sempre 2 casas decimais com `fixedDecimalScale`.
 *
 * Aceita:
 *   - Number        → 1650          → "R$ 1.650,00"
 *   - String "1650"                → "R$ 1.650,00"
 *   - String "R$ 1.650,00" (já formatada) → mantém como está
 *   - "" / null / undefined       → ""
 *
 * @param {number|string|null|undefined} valor
 * @returns {string} valor formatado (ou vazio se sem conteúdo numérico)
 */
export const formatarMoedaBR = (valor) => {
  if (valor === null || valor === undefined) return "";

  // Já está em formato BR? Devolve como veio, normalizando só o prefixo.
  if (typeof valor === "string") {
    const trimmed = valor.trim();
    if (trimmed === "") return "";

    // Extrai só dígitos para tentar detectar formato. Se tiver mais de uma
    // vírgula e nenhum ponto, é BR (centavos com vírgula, sem milhar).
    // Se tiver pontos E vírgula, BR (milhar ponto, centavo vírgula).
    // Se tiver só ponto, formato US — precisa converter.
    const soNumeros = trimmed.replace(/[^\d.,-]/g, "");
    if (soNumeros === "") return trimmed;

    const temVirgula = soNumeros.includes(",");
    const temPonto = soNumeros.includes(".");

    // Se já tem o padrão BR (R$ + milhar com ponto + centavo com vírgula
    // OU só centavos com vírgula), devolve como está.
    if (temVirgula && /^\d{1,3}(\.\d{3})*,\d{1,2}$/.test(soNumeros)) {
      return trimmed.startsWith("R$")
        ? trimmed
        : `R$ ${soNumeros}`;
    }

    // Se tem só ponto, interpreta como formato americano "1,650.00"
    // e converte para BR "1.650,00".
    if (!temVirgula && temPonto) {
      const [intPart, decPart = ""] = soNumeros.split(".");
      const intNumerico = intPart.replace(/\D/g, "");
      if (intNumerico === "") return "";
      const intComMilhar = intNumerico.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ".",
      );
      const decNumerico = (decPart || "").replace(/\D/g, "").padEnd(2, "0").slice(0, 2);
      return `R$ ${intComMilhar},${decNumerico || "00"}`;
    }

    // Só dígitos ou formato BR sem milhar: "1650" ou "1650,5".
    const digits = soNumeros.replace(/\D/g, "");
    if (digits === "") return "";
    const padded = digits.padStart(3, "0");
    const intPart = padded.slice(0, padded.length - 2).replace(/^0+/, "") || "0";
    const decPart = padded.slice(padded.length - 2);
    const intComMilhar = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `R$ ${intComMilhar},${decPart}`;
  }

  if (typeof valor === "number" && !isNaN(valor)) {
    const intPart = Math.trunc(valor).toString();
    const decPart = Math.round((valor - Math.trunc(valor)) * 100)
      .toString()
      .padStart(2, "0");
    const intComMilhar = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `R$ ${intComMilhar},${decPart}`;
  }

  return "";
};

// ============================================
// FUNÇÕES PURO DE CÁLCULO/REGRAS
// ============================================

/**
 * Calcula a data máxima permitida para o Término do estágio
 * (até 2 anos após o Início). Retorna string no formato YYYY-MM-DD
 * ou "" se o Início não estiver preenchido.
 *
 * @param {string} inicioEstagio - data no formato YYYY-MM-DD
 */
export const calcularMaxTerminoEstagio = (inicioEstagio) => {
  if (!inicioEstagio) return "";
  const [yy, mm, dd] = inicioEstagio.split("-").map(Number);
  if (!yy || !mm || !dd) return "";
  const limite = new Date(yy, mm - 1, dd);
  limite.setFullYear(limite.getFullYear() + 2);
  return [
    limite.getFullYear(),
    String(limite.getMonth() + 1).padStart(2, "0"),
    String(limite.getDate()).padStart(2, "0"),
  ].join("-");
};

/**
 * Verifica se hora e minuto formam um horário válido (00:00 a 23:59).
 *
 * @param {string|number} hora - hora (0-23)
 * @param {string|number} minuto - minuto (0-59)
 * @returns {boolean} true se for um horário válido
 */
export const horarioValido = (hora, minuto) => {
  const h = Number(hora);
  const m = Number(minuto);

  return !isNaN(h) && !isNaN(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59;
};

/**
 * Calcula horas decimais entre dois horários (considerando intervalo).
 * Suporta virada de meia-noite (24h wrap).
 *
 * @param {string} entrada - horário de entrada (HH:MM)
 * @param {string} saida - horário de saída (HH:MM)
 * @param {string} [intervaloInicio] - horário de início do intervalo
 * @param {string} [intervaloFim] - horário de fim do intervalo
 * @returns {number} total de horas decimais
 */
export function horasDecimais(
  entrada,
  saida,
  intervaloInicio = "",
  intervaloFim = "",
) {
  if (!entrada || !saida) return 0;

  const [eh, em] = entrada.split(":").map(Number);
  const [sh, sm] = saida.split(":").map(Number);
  let totalMin = sh * 60 + sm - (eh * 60 + em);

  if (totalMin < 0) totalMin += 24 * 60;

  if (intervaloInicio && intervaloFim) {
    const [ih, im] = intervaloInicio.split(":").map(Number);
    const [fh, fm] = intervaloFim.split(":").map(Number);
    let intervaloMin = fh * 60 + fm - (ih * 60 + im);
    if (intervaloMin < 0) intervaloMin += 24 * 60;
    totalMin -= intervaloMin;
  }

  return totalMin / 60;
}

/**
 * Valida uma estrutura de horários de estágio.
 *
 * Regras:
 *   - Carga horária diária não pode ultrapassar 6h
 *   - Jornada de 6h exige intervalo (qualquer um)
 *   - Carga horária semanal não pode ultrapassar 30h
 *
 * @param {object} horario - estrutura com { tipo, diasSelecionados, fixo, alternado }
 * @returns {{ errors: string[], totalSemanal: number }}
 */
export function validarHorarios(horario) {
  const errors = [];
  let totalSemanal = 0;

  if (horario.tipo === "fixo") {
    const f = horario.fixo;

    if (f.entrada && f.saida) {
      const horas = horasDecimais(
        f.entrada,
        f.saida,
        f.intervaloInicio,
        f.intervaloFim,
      );

      if (horas > 6)
        errors.push("Carga horária diária não pode ultrapassar 6h");
      if (horas >= 6 && (!f.intervaloInicio || !f.intervaloFim)) {
        errors.push("Intervalo obrigatório para jornada de 6h");
      }

      totalSemanal = horas * horario.diasSelecionados.length;
    }
  } else if (horario.tipo === "alternado") {
    const diasMap = {
      segunda: "Segunda",
      terca: "Terça",
      quarta: "Quarta",
      quinta: "Quinta",
      sexta: "Sexta",
      sabado: "Sábado",
      domingo: "Domingo",
    };

    Object.keys(horario.alternado).forEach((key) => {
      const diaLabel = diasMap[key];
      const h = horario.alternado[key];

      if (h.entrada && h.saida) {
        const horas = horasDecimais(
          h.entrada,
          h.saida,
          h.intervaloInicio,
          h.intervaloFim,
        );
        if (horas > 6) errors.push(`${diaLabel}: carga horária superior a 6h`);
        if (horas >= 6 && (!h.intervaloInicio || !h.intervaloFim)) {
          errors.push(`${diaLabel}: intervalo obrigatório para 6h`);
        }
        totalSemanal += horas;
      }
    });
  }

  if (totalSemanal > 30) {
    errors.push(
      `Carga horária semanal (${totalSemanal.toFixed(1)}h) superior ao limite de 30h`,
    );
  }
  return { errors, totalSemanal };
}

// ============================================
// DIAS DA SEMANA (LABELS PARA UI)
// ============================================



export const DIAS_SEMANA = [
  { label: "Segunda", key: "segunda" },
  { label: "Terça", key: "terca" },
  { label: "Quarta", key: "quarta" },
  { label: "Quinta", key: "quinta" },
  { label: "Sexta", key: "sexta" },
  { label: "Sábado", key: "sabado" },
  { label: "Domingo", key: "domingo" },
];

// Turnos possíveis para a seção "Dados Escolares" do TCE. Usado no
// select de Turno (preenchimento) e para validação/normalização
// caso o backend envie valor fora dessa lista.
export const TURNOS = [
  { label: "Matutino", value: "MATUTINO" },
  { label: "Vespertino", value: "VESPERTINO" },
  { label: "Noturno", value: "NOTURNO" },
  { label: "Integral", value: "INTEGRAL" },
];

/**
 * Extrai um array de CPFs normalizados (apenas dígitos) a partir
 * de um objeto de dados. Usado por hasDuplicateCPF.
 *
 * @param {object} data - objeto com campos de CPF
 * @param {string[]} fields - lista de chaves a extrair
 * @returns {string[]} array de CPFs normalizados (sem duplicatas vazias)
 */
export const extractCpfs = (data, fields) => {
  const values = fields.map((f) => normalizeCPF(data[f]));
  return values.filter((v) => v !== "");
};

/**
 * Verifica se há CPF duplicado entre os campos do objeto.
 * Refactor da função inline no componente para receber parâmetros
 * em vez de fechar sobre formData.
 *
 * @param {object} data - objeto com campos de CPF
 * @param {string[]} [fields] - opcional. Defaults aos campos conhecidos.
 * @returns {boolean} true se houver duplicata
 */
export const hasDuplicateCPF = (data, fields) => {
  // Regras:
  //  - cnpj_cpf e cpfCnpj (CNPJs matriz/filial) NÃO são comparados:
  //    empresa individual (MEI/EI) tem o mesmo CNPJ/CPF raiz e PJ
  //    tem filiais com CNPJ distinto (0001/0002/0003) — coincidência
  //    não é erro.
  //  - cpf_estagiario (supervisor) NÃO é comparado: pode ser o
  //    próprio representante legal da empresa.
  //  - cpf_estudante_estagiario x cpf_menor_idade SÓ são comparados
  //    quando o estudante for de fato MENOR de idade. Quando maior,
  //    cpf_menor_idade deve estar vazio — se o backend devolveu o
  //    CPF do estudante nesse campo por padrão, ignoramos.
  const cpfFields = fields || [
    "cpf_estudante_estagiario",
    "cpf_menor_idade",
  ];
  const dataCopy = { ...(data || {}) };

  // Se o estudante é maior de idade, NÃO inclui cpf_menor_idade na
  // checagem — esse campo não se aplica e o backend pode ter vindo
  // preenchido com o CPF do próprio estudante por default.
  const isMenor = String(
    dataCopy.maior_menor_idade || "",
  ).toUpperCase() === "MENOR";
  if (!isMenor) {
    dataCopy.cpf_menor_idade = "";
  }

  const filtered = extractCpfs(dataCopy, cpfFields);
  return new Set(filtered).size !== filtered.length;
};

/**
 * Verifica se o valor (de NumericFormat) é um número positivo.
 * Usado como validador customizado do NumericFormat.
 *
 * @param {object} values - objeto do NumericFormat { floatValue, formattedValue, ... }
 * @returns {boolean} true se o valor numérico for > 0
 *
 *   isPositiveNumber({ floatValue: 10 })            → true
 *   isPositiveNumber({ floatValue: 0 })             → false
 *   isPositiveNumber({ floatValue: undefined })     → false (campo vazio)
 *   isPositiveNumber({})                            → false (defensivo)
 *
 * Antes: `values.floatValue > 0` retornava `false` para `undefined`
 * (correto) mas `Number.isFinite(undefined)` é `false`, então tratamos
 * NaN/Infinity defensivamente para nunca travar a digitação.
 */
export const isPositiveNumber = (values) => {
  const n = Number(values?.floatValue);
  return Number.isFinite(n) && n > 0;
};

/**
 * Converte string monetária BR ("R$ 1.650,00") em número (1650.00).
 *
 * Usado para sanitizar valores antes do POST/PUT para o backend:
 * como `valor_bolsa_auxilio` / `valor_bolsa_transporte` ficam
 * armazenados em `formData` como string formatada para exibição,
 * o envio precisa ser numérico para que o backend grave corretamente
 * em colunas DECIMAL/FLOAT e para que a hidratação volte consistente.
 *
 *   "R$ 1.650,00"  → 1650.00
 *   "1.650,00"     → 1650.00
 *   "1650"          → 1650
 *   1650            → 1650
 *   "" / null / undefined → null
 *
 * @param {number|string|null|undefined} valor
 * @returns {number|null}
 */
export const moedaParaNumero = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;
  if (typeof valor === "number") {
    return Number.isFinite(valor) ? valor : null;
  }
  if (typeof valor !== "string") return null;

  // mantém só dígitos, vírgula e ponto; ignora R$, espaços, etc.
  const limpo = valor.replace(/[^\d,.-]/g, "");
  if (!limpo) return null;

  // Se tem vírgula, é padrão BR: "." é separador de milhar, "," é decimal.
  // Remove os pontos de milhar e troca a vírgula por ponto.
  if (limpo.includes(",")) {
    const normalizado = limpo.replace(/\./g, "").replace(",", ".");
    const num = Number(normalizado);
    return Number.isFinite(num) ? num : null;
  }

  // Sem vírgula: ou é inteiro ("1650") ou já está em formato US
  // ("1650.00"). Tenta direto.
  const num = Number(limpo);
  return Number.isFinite(num) ? num : null;
};

/**
 * Sanitiza um objeto `formData` convertendo TODOS os campos
 * monetários listados de string BR para número, retornando um novo
 * objeto (não muta o original).
 *
 * @param {object} formData - objeto do form
 * @param {string[]} campos - lista de chaves a sanitizar
 * @returns {object} cópia do formData com os campos convertidos
 */
export const sanitizarCamposMonetarios = (formData, campos) => {
  const copia = { ...(formData || {}) };
  campos.forEach((c) => {
    copia[c] = moedaParaNumero(copia[c]);
  });
  return copia;
};

